// Tarjoukset: tarjousnäkymien tietomalli (quotation) ↔ backendin tarjous (/api/tenant/offer).
// Tarjous kuuluu tarjottavaan projektiin, jolla on vain nimi (quotation.name); saman projektin muut tarjoukset ovat
// siblings-listassa. Tarjouksella on yksi tilaaja (recipient), jolla on oma tarjousnumero.
// Työmaan rivit ovat samat kuin backendissä: jokainen hinnoiteltava asia on oma rivinsä (työ, muu rivi, nimike, lisähinta).
// Työrivin määrä ja yksikkö lasketaan menetelmän hinnoitteluperusteesta (näkymän summat), backend laskee ne itse.
import axios from 'axios';
import dayjs from 'dayjs';
import Helper from '@/helper';
import workMethodsApi from '@/api/workMethods';
import itemApi from '@/api/item';
import partyApi from '@/api/party';
import personApi from '@/api/person';
import workerApi from '@/api/worker';
import UserService from '@/service/user.service';
import { pricedQuantity, pricedUnit } from '@/helpers/quotationCalc';
import { hasPurpose, loadItemTypes, PURPOSE } from '@/helpers/itemTypes';

export { STATUSES, ACTIVE_STATUSES, REASON_REQUIRED, blockers, warnings } from '@/helpers/quotationStatus';

const API = '/api/tenant/offer';

let workMethodsCache = null;
async function workMethods() {
  if (!workMethodsCache) workMethodsCache = await workMethodsApi.getWorkMethods();
  return workMethodsCache;
}

function dateTime(value) {
  return value ? dayjs(value).format('YYYY-MM-DDTHH:mm:ss') : null;
}

function attributeValueRequest(v) {
  return {
    defId: v.defId,
    valueNumber: v.valueNumber,
    valueText: v.valueText,
    valueBoolean: v.valueBoolean,
    valueDate: v.valueDate,
    valueItem: v.valueItem ? { id: v.valueItem.id } : null,
  };
}

// Uuden tarjouksen tilaaja: tarjouspyyntö saapui tänään sähköpostilla
export function emptyRecipient() {
  return {
    id: null,
    code: null,
    receivedDate: dayjs().format('YYYY-MM-DD'),
    channel: 'email',
    source: '',
    partyId: null,
    personId: null,
    sentDate: null,
    sentVersion: null,
    result: null,
    resultDate: null,
    resultReason: null,
    deadline: null,
    deadlineTime: null,
    dates: [],
    note: '',
  };
}

// --- backend → näkymät ---

function toRow(r, methods) {
  const method = methods.find(m => m.id === r.workMethodId);
  const work = r.rowType === 'WORK';
  return {
    id: r.id,
    rowType: r.rowType,
    workMethodId: r.workMethodId || null,
    attributeValues: r.attributeValues || [],
    item: r.item || null,
    description: r.description || '',
    quantity: work ? pricedQuantity(method, r.attributeValues) : r.quantity,
    unit: work ? pricedUnit(method) : r.unit || '',
    unitPrice: r.unitPrice,
    conditional: !!r.conditional,
    conditionText: r.conditionText || '',
  };
}

function toSite(s, index, methods) {
  return {
    id: s.id,
    name: s.name || '',
    address: s.address || '',
    // Sama muoto kuin työmaan geocoding, jotta MapDialog toimii sellaisenaan
    geocoding: { lat: s.lat ?? null, lng: s.lng ?? null, address: s.geocodedAddress || '' },
    additionalInfo: s.workDescription || '',
    soilTypes: (s.soilType || '')
      .split(',')
      .map(t => t.trim())
      .filter(Boolean),
    // Vapaa aikataulu ei näy näkymissä (aloitus ja kesto ovat tarjouksella), mutta säilyy tallennuksessa
    schedule: s.schedule || null,
    sortOrder: (index + 1) * 10,
    rows: (s.rows || []).map(r => toRow(r, methods)),
  };
}

function toRecipient(r) {
  // Ennen yhden tilaajan mallia luodulla tarjouksella ei välttämättä ole tilaajaa
  if (!r) return emptyRecipient();
  return {
    id: r.id,
    code: r.code,
    receivedDate: r.rfqReceivedDate,
    channel: r.rfqChannel || 'email',
    source: r.rfqSource || '',
    partyId: r.party ? r.party.id : null,
    personId: r.person ? r.person.id : null,
    sentDate: r.sentDate,
    sentVersion: r.sentVersion,
    result: r.result,
    resultDate: r.resultDate,
    resultReason: r.resultReason,
    deadline: r.deadline,
    deadlineTime: r.deadlineTime ? r.deadlineTime.slice(0, 5) : null,
    dates: (r.dates || []).map(d => ({ title: d.title, dueDate: d.dueDate, doneDate: d.doneDate })),
    note: r.note || '',
  };
}

function fromOffer(o, methods) {
  return {
    id: o.id,
    name: o.name,
    offerProjectId: o.offerProjectId,
    siblings: o.siblings || [],
    status: o.status,
    version: o.version || 1,
    authorUserId: o.responsiblePerson ? o.responsiblePerson.id : null,
    request: {
      description: o.description || '',
      desiredStart: o.desiredStartDate,
      plannedEnd: o.plannedEndDate,
    },
    sites: (o.sites || []).map((s, i) => toSite(s, i, methods)),
    texts: {
      intro: o.textIntro || '',
      included: o.priceIncludes || '',
      reservations: o.reservations || '',
      obligations: o.customerResponsibilities || '',
      other: o.otherTerms || '',
      contact: o.textContact || '',
    },
    paymentTerm: o.paymentTerm || '14',
    paymentTermText: o.paymentTermText || '',
    duration: o.duration || '',
    recipient: toRecipient(o.recipient),
    calculationNotes: o.internalNote || '',
    costEstimate: o.costEstimate,
    acceptedProject: o.projectId
      ? {
          id: o.projectId,
          code: o.projectCode,
          name: o.projectName,
          siteCount: (o.sites || []).length,
          ordererName: o.recipient && o.recipient.party ? o.recipient.party.companyName : '',
        }
      : null,
    createdAt: dateTime(o.createdAt),
    updatedAt: dateTime(o.updatedAt),
  };
}

// --- näkymät → backend ---

// Työrivin määrä ja yksikkö tulevat menetelmän parametrista; nimike vain nimikeriville
function rowRequest(r) {
  const work = r.rowType === 'WORK';
  return {
    rowType: r.rowType,
    workMethodId: work ? r.workMethodId || null : null,
    itemId: r.rowType === 'ITEM' && r.item ? r.item.id : null,
    description: r.description,
    quantity: work ? null : r.quantity,
    unit: work ? null : r.unit,
    unitPrice: r.unitPrice,
    conditional: !!r.conditional,
    conditionText: r.conditional ? r.conditionText : null,
    attributeValues: work ? (r.attributeValues || []).map(attributeValueRequest) : [],
  };
}

const bySortOrder = (a, b) => (a.sortOrder || 0) - (b.sortOrder || 0);

function toRequest(q) {
  const sites = [...(q.sites || [])].sort(bySortOrder);
  const r = q.recipient;
  return {
    name: q.name,
    description: q.request.description,
    responsiblePersonId: q.authorUserId || null,
    desiredStartDate: q.request.desiredStart,
    plannedEndDate: q.request.plannedEnd,
    textIntro: q.texts.intro,
    priceIncludes: q.texts.included,
    reservations: q.texts.reservations,
    customerResponsibilities: q.texts.obligations,
    otherTerms: q.texts.other,
    textContact: q.texts.contact,
    paymentTerm: q.paymentTerm,
    paymentTermText: q.paymentTermText,
    duration: q.duration,
    costEstimate: q.costEstimate,
    internalNote: q.calculationNotes,
    recipient: {
      partyId: r.partyId,
      personId: r.personId,
      rfqReceivedDate: r.receivedDate || null,
      rfqChannel: r.channel || null,
      rfqSource: r.source || null,
      result: r.result || null,
      resultDate: r.resultDate || null,
      resultReason: r.resultReason || null,
      note: r.note || null,
      deadline: r.deadline || null,
      deadlineTime: r.deadlineTime || null,
      sentDate: r.sentDate || null,
      dates: (r.dates || []).map(d => ({ title: d.title, dueDate: d.dueDate, doneDate: d.doneDate || null })),
    },
    sites: sites.map(s => ({
      name: s.name,
      address: s.address,
      lat: s.geocoding?.lat ?? null,
      lng: s.geocoding?.lng ?? null,
      geocodedAddress: s.geocoding?.address || null,
      workDescription: s.additionalInfo,
      soilType: (s.soilTypes || []).join(', '),
      schedule: s.schedule || null,
      rows: (s.rows || []).map(rowRequest),
    })),
  };
}

// --- lista ---

// Kirjautuneen käyttäjän henkilö (tarjouksen laatija on henkilö); käyttäjällä ilman työntekijää ei ole omia tarjouksia
async function currentPersonId() {
  const user = UserService.getUserInfo();
  if (!user || !user.id) return null;
  try {
    const worker = await workerApi.getWorker(user.id);
    return worker && worker.person ? worker.person.id : null;
  } catch (e) {
    return null;
  }
}

// Vain omat: henkilötön käyttäjä ei näe yhtään tarjousta (-1 ei ole kenenkään henkilö)
async function responsiblePersonId(onlyMine) {
  if (!onlyMine) return null;
  return (await currentPersonId()) || -1;
}

async function offerDTO(request) {
  const [offer, methods] = await Promise.all([axios(request).then(r => r.data), workMethods()]);
  return fromOffer(offer, methods);
}

const api = {
  // Tarjottavat projektit ja niiden tarjoukset. filter: { search, status, year, onlyMine }
  async getQuotationsPaged(filter = {}, options = {}) {
    return axios({
      url: `${API}/paged`,
      method: 'post',
      params: { ...Helper.tableOptionsToRestPageParams(options) },
      data: {
        search: filter.search || null,
        statuses: filter.status && filter.status.length > 0 ? filter.status : null,
        year: filter.year || null,
        responsiblePersonId: await responsiblePersonId(filter.onlyMine),
      },
    }).then(r => r.data);
  },
  // Keskeneräisten tarjousten avoimet määräpäivät päivämääräjärjestyksessä
  async getUpcomingDates({ onlyMine = false } = {}) {
    return axios({ url: `${API}/dates`, params: { responsiblePersonId: await responsiblePersonId(onlyMine) } }).then(r => r.data);
  },
  // Projektihaku nimellä: [{ id, name, offers: [{ id, code, partyName }] }]
  async searchProjects(search) {
    return axios({ url: `${API}/projects`, params: { search } }).then(r => r.data);
  },
  // Esitäytetty tarjous-Word (jsreport-pohja); ei tallennu tarjoukselle
  async downloadWord(id, recipientId) {
    return axios({ url: `${API}/${id}/recipients/${recipientId}/word`, responseType: 'blob' }).then(r => r.data);
  },
  // (Muokattu) Word tarjoukselle: siitä tehdään PDF. Palauttaa { word, pdf }; korvaa aiemmat.
  async importWord(id, recipientId, file) {
    const data = new FormData();
    data.append('file', file);
    return axios({ url: `${API}/${id}/recipients/${recipientId}/word`, method: 'post', data }).then(r => r.data);
  },
  // Liite tarjoukselle; folder = raahatun kansion polku. Palvelin purkaa zipin kansioiksi
  async uploadFile(id, file, folder = null) {
    const data = new FormData();
    data.append('file', file);
    return axios({ url: `${API}/${id}/files`, method: 'post', params: { folder }, data }).then(r => r.data);
  },
  // Liitteet zippinä; folder = kansion polku, null = kaikki
  async downloadFilesZip(id, folder) {
    return axios({ url: `${API}/${id}/files/zip`, params: { folder }, responseType: 'blob' }).then(r => r.data);
  },
  // Tilattujen tarjousten tarjouspyyntöaineisto projektin työmaille
  async getProjectMaterial(projectId) {
    return axios({ url: `${API}/material`, params: { projectId } }).then(r => r.data);
  },
  async downloadProjectMaterialZip(projectId, folder) {
    return axios({ url: `${API}/material/zip`, params: { projectId, folder }, responseType: 'blob' }).then(r => r.data);
  },
  // Tarjoukset, joista projekti on perustettu: [{ id, code }]
  async getProjectOffers(projectId) {
    return axios({ url: `${API}/by-project`, params: { projectId } }).then(r => r.data);
  },
  // Tärkeä päivä kuitataan tehdyksi tänään
  async markDateDone(id, dateIndex) {
    const q = await api.getQuotation(id);
    const date = q.recipient.dates[dateIndex];
    if (!date) {
      const err = new Error('Päivää ei löydy');
      err.response = { status: 400, data: { message: err.message } };
      throw err;
    }
    date.doneDate = dayjs().format('YYYY-MM-DD');
    return api.updateQuotation(id, q);
  },
  // Lukumäärät ja summat vaiheittain, voitto-% ja vuodet, joilta tarjouksia on
  async getSummary(year) {
    return axios({ url: `${API}/summary`, params: { year: year || null } }).then(r => r.data);
  },
  async getQuotation(id) {
    return offerDTO({ url: `${API}/${id}` });
  },
  async createQuotation(data) {
    return offerDTO({ url: API, method: 'post', data: toRequest(data) });
  },
  async updateQuotation(id, data) {
    return offerDTO({ url: `${API}/${id}`, method: 'put', data: toRequest(data) });
  },
  // reason: { code, text } syy-dialogista
  async changeStatus(id, status, reason) {
    const reasonText = reason ? [reason.code, reason.text].filter(Boolean).join(' – ') : null;
    return offerDTO({ url: `${API}/${id}/status`, method: 'put', data: { status, reason: reasonText } });
  },
  // Uusi tarjous kopiona palvelimella. opts: { partyId, personId, deadline, includeSites, includeTexts, includeConditional,
  // newProjectName }; ilman newProjectNamea kopio tulee samaan projektiin.
  async copyQuotation(id, opts) {
    return offerDTO({ url: `${API}/${id}/copy`, method: 'post', data: opts });
  },
  async moveQuotation(id, offerProjectId) {
    return offerDTO({ url: `${API}/${id}/move`, method: 'put', data: { offerProjectId } });
  },
  async detachQuotation(id, name) {
    return offerDTO({ url: `${API}/${id}/detach`, method: 'post', data: { name } });
  },
  // Tarkistuspyyntö sähköpostilla toiselle Hallinto-käyttäjälle; tarjous ei muutu
  async requestReview(id, userId, message) {
    return axios({ url: `${API}/${id}/review-request`, method: 'post', data: { userId, message } }).then(r => r.data);
  },
  // payload: { orderDate, orderReference, projectName }. Kaikki työmaat perustetaan (tyhjä lista = kaikki)
  // ja niille asetetaan suunniteltu aloitus ja kesto tarjouksen tiedoista.
  async acceptQuotation(id, payload) {
    return offerDTO({
      url: `${API}/${id}/project`,
      method: 'post',
      data: {
        role: 'ORDERER',
        projectName: payload.projectName,
        orderDate: payload.orderDate,
        orderReference: payload.orderReference,
        setSchedule: true,
        sites: [],
      },
    });
  },
  // data: { sentDate?, result?, resultDate?, resultReason?, dates? }. Tallentaa tilaajan tiedot heti tallennetulle
  // tarjoukselle. markReady: keskeneräinen sisältö merkitään ensin valmiiksi (lähetys ennen Valmis-vaihetta).
  async updateRecipient(id, data, { markReady = false } = {}) {
    let q = await api.getQuotation(id);
    if (markReady && ['CALCULATION', 'REVIEW'].includes(q.status)) q = await api.changeStatus(id, 'READY', null);
    Object.assign(q.recipient, data);
    return api.updateQuotation(id, q);
  },
  async deleteQuotation(id) {
    return axios({ url: `${API}/${id}`, method: 'delete' }).then(r => r.data);
  },

  async getWorkMethods() {
    return workMethods();
  },
  async getParties() {
    return partyApi.getParties();
  },
  async getPersons() {
    const page = await personApi.getPersonsPaged({}, { itemsPerPage: -1 });
    return page.content || page;
  },
  async getItems() {
    // Menetelmät ovat legacy-syistä myös nimikkeitä; ne eivät kuulu hintariveille
    const [items] = await Promise.all([itemApi.getItems(), loadItemTypes()]);
    return items.filter(i => i.active !== false && !hasPurpose(i.typeId, PURPOSE.DRILLING_METHOD));
  },
  // Laatija = tarjouksen vastuuhenkilö (työntekijöiden henkilöt)
  async getUsers() {
    const workers = await workerApi.getWorkers();
    const persons = workers.filter(w => w.person).map(w => w.person);
    return persons
      .filter((p, i) => persons.findIndex(x => x.id === p.id) === i)
      .map(p => ({ id: p.id, firstName: p.firstname, lastName: p.lastname, email: p.email, phone: p.phone }));
  },
};

export default api;
